import Swal from "sweetalert2";
import PropTypes from 'prop-types';
import styled from 'styled-components';
import EditIcon from 'mdi-react/EditIcon';
import { Spinner } from 'react-bootstrap';
import Badge from '@/shared/components/Badge';
import DeleteIcon from 'mdi-react/DeleteIcon';
import React, { useState, useEffect } from 'react';
import AdminInterface from '@/utils/AdminInterface';
import withReactContent from "sweetalert2-react-content";
import { paddingLeft, paddingRight } from '@/utils/directions';
import ReactTableBase from '@/shared/components/table/ReactTableBase';
import { colorAdditional, colorGreenHover, colorAccent } from '@/utils/palette';
import { Card, CardBody, CardTitleWrap, CardTitle } from '@/shared/components/Card';

const CartTableButton = styled.button`
  background: transparent;
  border: none;
  line-height: 14px;
  ${paddingLeft}: 20px;
  ${paddingRight}: 0;
  padding-top: 0;
  padding-bottom: 0;
  color: ${colorAdditional};
  cursor: pointer;
  transition: all 0.3s;

  svg {
    height: 24px;
    width: 24px;
    fill: '#ddd';
    transition: all 0.3s;
  }

  svg:hover {
    fill: ${colorGreenHover};
  }

  .orange-hover:hover {
    fill: #e9c886;
  }

  .red-hover:hover {
    fill: #e98686;
  }
`;

const TableSpinner = styled(Spinner)`
  color: ${colorAccent};
`;

const StatusFormatter = value => (
    value ? <Badge bg="success">Enable</Badge> : <Badge bg="secondary">Disable</Badge>
);

StatusFormatter.propTypes = {
    value: PropTypes.bool.isRequired,
};

const CalibrationsList = (props) => {
    const [showLoader, setShowLoader] = useState(true);
    const [calibrationsList, setCalibrationsList] = useState({ tableHeaderData: [], tableRowsData: [] });
    const tableConfig = {
        isEditable: false,
        isSortable: true,
        isResizable: false,
        withPagination: false,
        withSearchEngine: true,
        placeholder: 'Search...',
    };

    const handleEdit = (calibration) => {
        props.setSelectedCalibration(calibration)
    }

    const handleDelete = (calibration) => {
        Swal.fire({
            title: 'Warning!',
            text: `Do you want to delete the calibration ${calibration.id}?`,
            icon: 'warning',
            confirmButtonText: 'Yes!',
            cancelButtonText: 'No!',
            showCancelButton: true
        }).then((result) => {
            if (result.isConfirmed) {
                AdminInterface.instance.deleteCalibration(calibration.id).then(() => {
                    withReactContent(Swal).fire({
                        title: "Success",
                        html: "Calibration is deleted!",
                    });
                    props.setListRequiresReload(true);
                }).catch((error) => {
                    withReactContent(Swal).fire({
                        title: "Error",
                        html: error.response?.data?.detail,
                    });
                })
            }
        });
    }

    const GetCalibrations = () => {
        setShowLoader(true);
        AdminInterface.instance.getCalibrations().then((response) => {
            const columns = [
                {
                    accessor: 'id',
                    Header: 'Id',
                },
                {
                    accessor: 'description',
                    Header: 'Description',
                },
                {
                    accessor: 'calibration_date',
                    Header: 'Calibration Date',
                    disableGlobalFilter: false,
                },
                {
                    accessor: 's3_uri',
                    Header: 'S3 URI',
                    disableGlobalFilter: true,
                },
                {
                    accessor: 'edit',
                    Header: 'Edit',
                    disableGlobalFilter: true,
                    disableSortBy: true,
                },
                {
                    accessor: 'delete',
                    Header: 'Delete',
                    disableGlobalFilter: true,
                    disableSortBy: true,
                }
            ];
            const data = [];
            response.data.forEach(calibration => {
                data.push({
                    id: calibration.id,
                    description: calibration.description,
                    calibration_date: new Date(`${calibration.calibration_date}z`).toLocaleString(),
                    s3_uri: <a href={calibration.s3_uri} target="_blank">{calibration.s3_uri}</a>,
                    edit: <CartTableButton type="button" onClick={() => handleEdit(calibration)}><EditIcon className="orange-hover"/></CartTableButton>,
                    delete: <CartTableButton type="button" onClick={() => handleDelete(calibration)}><DeleteIcon className="red-hover"/></CartTableButton>,
                });
            });

            setCalibrationsList({ tableHeaderData: columns, tableRowsData: data });
        }).catch((error) => {
            withReactContent(Swal).fire({
                title: "Error",
                html: error.response?.data?.detail,
            });
        }).finally(() => {
            setShowLoader(false);
            props.setListRequiresReload(false);
        });
    }

    useEffect(() => {
        GetCalibrations();
    }, [props.listRequiresReload]);

    return (
        <Card>
            <CardBody>
                <CardTitleWrap>
                    <CardTitle>Calibrations List</CardTitle>
                </CardTitleWrap>
                {
                    !showLoader &&
                    <ReactTableBase columns={calibrationsList.tableHeaderData} data={calibrationsList.tableRowsData} tableConfig={tableConfig} />
                }
                {showLoader && (<div className="text-center"><TableSpinner animation="border" /></div>)}
            </CardBody>
        </Card>
    );
};

export default CalibrationsList;